import * as firebase from "firebase";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Document } from "@/models/documents/Document";
import { deleteFileByPath, optimizePhotos, uploadImage, uploadImageAsync } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import { v4 as uuidv4 } from "uuid";
import {GalleryImage} from "@/models/GalleryImage";
import {waitResizedImage} from "@/utils/wait-resized-image";

export default {
  name: "DocumentsAddDlg",
  components: { ValidationObserver, ValidationProvider, GalleryPhotosUploadDlg },
  data() {
    return {
      dialog: false,
      document: new Document(this.$store.state.authModule.user.id, this.$store.state.authModule.user.name),
      saveLoading: false,
      isInvalidFile: false,
      selectedPdf: null,
      selectedImage: null,
      imagePreviewUrl: require('@/assets/no-image.jpg'),
      imgLoading: false,
      facilitiesToSelect: [],
      selectedFacilitiesIds: [],
    }
  },
  computed: {
    user() {
      return this.$store.state.authModule.user;
    },
    facilityId() {
      return this.$store.state.authModule.currentFacility;
    },
    facilities() {
      return this.$store.state.facilityModule.facilities;
    },
    categories() {
      return this.$store.state.documentsModule.documentsCategories;
    },
  },
  watch: {
    facilities(list) {
      if (list) {
        this.facilitiesToSelect = list.filter(x => x.id != this.facilityId)
      }
    }
  },
  methods: {
    open() {
      this.dialog = true;
    },
    close() {
      this.document.clear();
      this.document.isEnabled = true;
      this.$refs.inputImgUpload.value = null;
      this.selectedPdf = null;
      this.selectedFacilitiesIds = [];
      this.$refs.createDocumentForm.reset()
      this.dialog = false;
    },
    async uploadFile(file, documentId, facilityId) {
      if (!file) return;

      try {
        const storageRef = firebase.storage().ref()
        const filesRef = storageRef.child(facilityId).child('documents')

        let type = file.extension || file.type;
        if (type === 'pdf') {
          type = file.type;
        }

        const uploadFileRef = filesRef.child(documentId).child(`${file.name}`)

        return await uploadFileRef.put(file, { contentType: type }).then(async () => {
          return await uploadFileRef.getDownloadURL().then(async (url) => {
            const update = { pdfUrl: url }
            return await firebase.firestore().collection("documents").doc(documentId).update(update)
          })
        })

      } catch (error) {
        this.saveLoading = true
        return;
      }
    },
    async uploadContentImage(file, documentId, facilityId) {
      const path = `${facilityId}/documents/${documentId}/${uuidv4()}`;
      this.selectedImage.path = path
      return await uploadImageAsync({
        path,
        file
      }).then(async (storage) => {
        const uploadedImageUrl = await storage.ref.getDownloadURL();
        const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

        try {
          const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
          this.document.imageUrl = uploadedImageUrl
          this.document.thumbnailUrl = resizedImageUrl
        } catch (e) {
          this.document.imageUrl = uploadedImageUrl;
          this.document.thumbnailUrl = uploadedImageUrl;
        }

        await firebase.firestore().collection("documents").doc(documentId).update({
          imageUrl: this.document.imageUrl,
          thumbnailUrl: this.document.thumbnailUrl
        }).then(async () => {
          const preloadPath = this.selectedImage?.preloadPath
          if (preloadPath) {
            await deleteFileByPath(`${preloadPath}`)
            await deleteFileByPath(`${preloadPath}_200x200`)
            this.selectedImage.preloadPath = ''
          }
        })
      });
    },
    async createDocument() {
      const isValid = await this.$refs.observer.validate()

      if (!isValid) return

      this.saveLoading = true;
      this.document.dateTimeCreated = firebase.firestore.Timestamp.fromDate(new Date());
      this.document.dateTimeUpdated = firebase.firestore.Timestamp.fromDate(new Date());

      const selectedFacilitiesIds = this.selectedFacilitiesIds.concat([this.facilityId]);

      for (const facilityId of selectedFacilitiesIds) {
        this.document.facilityId = facilityId;

        await this.$store.dispatch('addDocument', this.document)
          .then(async (response) => {
            const docId = response.documentId;
            const promises = [];

            if (this.selectedPdf) {
              const pdfPromise = this.uploadFile(this.selectedPdf, docId, facilityId);
              promises.push(pdfPromise);
            }

            if (this.selectedImage && this.selectedImage.file) {
              const imagePromise = this.uploadContentImage(this.selectedImage.file, docId, facilityId);
              promises.push(imagePromise);
            }

            await Promise.all(promises);
        }).catch((err) => {
          this.$toast.open({
            type: "error",
            message: this.$t("GeneralTranslations.UnhandledError")
          })
          console.log(err.message)
        })
      }

      this.$toast.open({
        type: "success",
        message: this.$t("AddDocument.DocumentSuccessAdded")
      });

      this.$store.dispatch("getDocuments");

      this.$refs.inputImgUpload.value = null;
      this.selectedPdf = null;

      this.saveLoading = false;

      this.close();
    },
    async saveImage(e) {
      const file = await optimizePhotos(e.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputImgUpload.value = null;
        return;
      }

      this.selectedImage = { file: file, path: '', preloadPath: '' }
      this.preloadContentImage(this.selectedImage.file, this.currentFacility)
    },
    async preloadContentImage(file, facilityId) {
      const path = `${facilityId}/documents/tmp/${uuidv4()}`;
      this.selectedImage.preloadPath = path
      uploadImage({
        path, file,
        onStart: () => {
          this.imgLoading = true;
        },
        onComplete: async storage => {
          this.imagePreviewUrl = await storage.snapshot.ref.getDownloadURL();
          this.imgLoading = false;
        }
      });
    },
    getExtension(filename) {
      const parts = filename.split('.')
      return parts[parts.length - 1]
    },
    onPdfSelect(event) {
      this.isInvalidFile = this.getExtension(event.target.files[0].name).toLowerCase() != 'pdf';
      if (!this.isInvalidFile) {
        this.selectedPdf = event.target.files[0];
      }
    },
    onCategoryChange() {
      this.document.categories = this.categories.filter(x => this.document.categoryIds.includes(x.id)).map(x => ({ id: x.id, name: x.name })) || []
    },
    onFacilityChange(facility, isSelected) {
      if (isSelected) {
        this.selectedFacilitiesIds.push(facility.id)
      }
      else {
        this.selectedFacilitiesIds.splice(this.selectedFacilitiesIds.indexOf(facility.id), 1)
      }
    },
    openGalleryPhotos() {
      (this.$refs.GalleryPhotosUploadDlg as any).open();
    },
    uploadGalleryPhoto(image: GalleryImage) {
      this.document.imageUrl = image.image;
      this.imagePreviewUrl = image.image;
      this.document.thumbnailUrl = image?.thumbnailUrl ?? image.image;
    },
  }
}